import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createFloorInwardRequestApi,
  fetchPendingFloorInwardsApi,
  fetchUserWarehouseStockApi,
  fetchFloorInwardHistoryApi,
  approveFloorInwardChangeRequestApi,
  cancelFloorInwardRequestApi,
  fetchPaginatedFloorStockApi,
  getProductRawMaterialsApi,
  getMaterialsRawMaterialsApi,
} from "../../api/warehouse.api";
import { UnitType } from "../../models/warehouseRawMaterial.model";
import ResponseModel from "../../models/response.model";

export interface IHistory {
  status: string;
  date: string;
  remark: string;
  userId: { _id: string; username: string };
  suggestedQuantity?: number;
}

export interface FloorInwardRequest {
  _id?: string;
  floorRequestId?: number;
  productId: { _id: string; name: string; sku: string; unit: UnitType };
  userId?: { _id: string; username: string };
  quantity: number;
  unit: UnitType;
  status: string;
  remark?: string;
  createdAt?: string;
  suggestedQuantity?: number;
  fulfilledQuantity?: number;
  history: IHistory[];
}

export interface WarehouseStock {
  _id: string;
  name: string;
  sku: string;
  unit: UnitType;
  minimumQuantity?: number;
  approxDeliveryDays?: number;
  quantity: number;
}

export interface FloorInwardState {
  pendingRequests: FloorInwardRequest[];
  stock: WarehouseStock[];
  history: FloorInwardRequest[];
  loading: boolean;
  error: string | null;
  stockTotalItems: number;
  stockTotalPages: number;
  stockPage: number;
  stockLimit: number;
  pendingTotalItems: number;
  pendingTotalPages: number;
  pendingPage: number;
  pendingLimit: number;
  historyTotalItems: number;
  historyTotalPages: number;
  historyPage: number;
  historyLimit: number;
  productRawMaterials: ProductRawMaterial[];
  materialsRawMaterials: ProductRawMaterial[];
}

export interface ProductRawMaterial {
  id: string;
  name: string;
  quantity: { value: number; unit: string };
  unitType: string;
}

// export const createFloorInwardRequest = createAsyncThunk<
//   FloorInwardRequest[],
//   {
//     requests: {
//       productId: string;
//       quantity: number;
//       unit: UnitType;
//       type: string;
//       remark?: string;
//     }[];
//     isDirectStoreRequest?: boolean;
//   },
//   { rejectValue: string }
// >(
//   "floorInward/createFloorInwardRequest",
//   async ({ requests, isDirectStoreRequest = false }, { rejectWithValue }) => {
//     try {
//       return await createFloorInwardRequestApi(requests, isDirectStoreRequest);
//     } catch (err: any) {
//       return rejectWithValue(
//         err.message || "Failed to create floor inward requests",
//       );
//     }
//   },
// );

export const createFloorInwardRequest = createAsyncThunk<
  ResponseModel,
  {
    items: {
      productId: string;
      quantity: number;
      unit: UnitType;
      type: string;
      remark?: string;
    }[];
    quantity: number;
    productId?: string;
    rawMaterialId?: string;
    assignee: string;
  },
  { rejectValue: string }
>(
  "floorInward/createFloorInwardRequest",
  async (
    { items, assignee, productId, rawMaterialId, quantity },
    { rejectWithValue },
  ) => {
    try {
      return await createFloorInwardRequestApi(
        items,
        assignee,
        productId,
        quantity,
        rawMaterialId,
      );
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create floor inward requests",
      );
    }
  },
);
export const fetchPendingFloorInwards = createAsyncThunk<
  {
    items: FloorInwardRequest[];
    totalPendingInwardRequests: number;
    currentPageNumber: number;
    itemsPerPage: number;
    totalPages: number;
  },
  { pageNumber?: number; itemsPerPage?: number; search?: string }
>(
  "floorInward/fetchPendingFloorInwards",
  async (
    { pageNumber = 1, itemsPerPage = 10, search = "" },
    { rejectWithValue },
  ) => {
    try {
      return await fetchPendingFloorInwardsApi({
        pageNumber,
        itemsPerPage,
        search,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch pending floor inwards",
      );
    }
  },
);

export const fetchUserWarehouseStock = createAsyncThunk<WarehouseStock[]>(
  "floorInward/fetchUserWarehouseStock",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchUserWarehouseStockApi();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch warehouse stock");
    }
  },
);

export const getProductRawMaterials = createAsyncThunk<
  ProductRawMaterial[],
  string
>(
  "floorInward/getProductRawMaterials",
  async (productId, { rejectWithValue }) => {
    try {
      return await getProductRawMaterialsApi(productId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch product raw materials",
      );
    }
  },
);

export const getMaterialsRawMaterials = createAsyncThunk<
  ProductRawMaterial[],
  string
>(
  "floorInward/getMaterialsRawMaterials",
  async (materialId, { rejectWithValue }) => {
    try {
      return await getMaterialsRawMaterialsApi(materialId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch material raw materials",
      );
    }
  },
);

export const fetchFloorInwardHistory = createAsyncThunk<
  {
    items: FloorInwardRequest[];
    totalFloorInwardRequests: number;
    currentPageNumber: number;
    itemsPerPage: number;
    totalPages: number;
  },
  {
    startDate?: string;
    endDate?: string;
    pageNumber?: number;
    itemsPerPage?: number;
    search?: string;
  }
>(
  "floorInward/fetchFloorInwardHistory",
  async (
    { startDate, endDate, pageNumber = 1, itemsPerPage = 10, search = "" },
    { rejectWithValue },
  ) => {
    try {
      return await fetchFloorInwardHistoryApi({
        startDate,
        endDate,
        pageNumber,
        itemsPerPage,
        search,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch floor inward history",
      );
    }
  },
);

export const cancelFloorInwardRequest = createAsyncThunk<
  FloorInwardRequest,
  string,
  { rejectValue: string }
>(
  "floorInward/cancelFloorInwardRequest",
  async (requestId, { rejectWithValue }) => {
    try {
      return await cancelFloorInwardRequestApi(requestId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to cancel floor inward request",
      );
    }
  },
);

export const approveFloorInwardRequest = createAsyncThunk<
  FloorInwardRequest,
  string,
  { rejectValue: string }
>(
  "floorInward/approveFloorInwardRequest",
  async (requestId, { rejectWithValue }) => {
    try {
      return await approveFloorInwardChangeRequestApi(requestId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to approve floor inward request",
      );
    }
  },
);

export const fetchPaginatedFloorStock = createAsyncThunk<
  { data: WarehouseStock[]; totalItems: number; totalPages: number },
  { page: number; limit: number; search?: string; warehouseId?: string },
  { rejectValue: string }
>(
  "floorInward/fetchPaginatedWarehouseStock",
  async ({ page, limit, search, warehouseId }, { rejectWithValue }) => {
    try {
      const response = await fetchPaginatedFloorStockApi({
        page,
        limit,
        search,
        warehouseId,
      });
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch paginated warehouse stock",
      );
    }
  },
);

const initialState: FloorInwardState = {
  pendingRequests: [],
  stock: [],
  history: [],
  loading: false,
  error: null,
  stockTotalItems: 0,
  stockTotalPages: 0,
  stockPage: 1,
  stockLimit: 10,
  pendingTotalItems: 0,
  pendingTotalPages: 0,
  pendingPage: 1,
  pendingLimit: 10,
  historyTotalItems: 0,
  historyTotalPages: 0,
  historyPage: 1,
  historyLimit: 10,
  productRawMaterials: [],
  materialsRawMaterials: [],
};

const floorInwardSlice = createSlice({
  name: "floorInward",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFloorInwardRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFloorInwardRequest.fulfilled, (state, action) => {
        state.loading = false;
        // state.pendingRequests.push(...action.payload);
      })
      .addCase(createFloorInwardRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPendingFloorInwards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingFloorInwards.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = action.payload.items;
        state.pendingTotalItems = action.payload.totalPendingInwardRequests;
        state.pendingTotalPages = action.payload.totalPages;
        state.pendingPage = action.payload.currentPageNumber;
        state.pendingLimit = action.payload.itemsPerPage;
      })
      .addCase(fetchPendingFloorInwards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserWarehouseStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserWarehouseStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stock = action.payload;
      })
      .addCase(fetchUserWarehouseStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFloorInwardHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFloorInwardHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload.items;
        state.historyTotalItems = action.payload.totalFloorInwardRequests;
        state.historyTotalPages = action.payload.totalPages;
        state.historyPage = action.payload.currentPageNumber;
        state.historyLimit = action.payload.itemsPerPage;
      })
      .addCase(fetchFloorInwardHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(approveFloorInwardRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveFloorInwardRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = state.pendingRequests.filter(
          (req) => req._id !== action.payload._id,
        );
        state.history = [action.payload, ...state.history];
      })
      .addCase(approveFloorInwardRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to approve request";
      })
      .addCase(cancelFloorInwardRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelFloorInwardRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = state.pendingRequests.filter(
          (req) => req._id !== action.payload._id,
        );
        state.history = [action.payload, ...state.history];
      })
      .addCase(cancelFloorInwardRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to cancel request";
      })
      .addCase(fetchPaginatedFloorStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaginatedFloorStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stock = action.payload.data;
        state.stockTotalItems = action.payload.totalItems;
        state.stockTotalPages = action.payload.totalPages;
      })
      .addCase(fetchPaginatedFloorStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch warehouse stock";
      })

      .addCase(getProductRawMaterials.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductRawMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.productRawMaterials = action.payload;
      })
      .addCase(getProductRawMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.productRawMaterials = [];
      })

      .addCase(getMaterialsRawMaterials.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMaterialsRawMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.materialsRawMaterials = action.payload;
      })
      .addCase(getMaterialsRawMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.materialsRawMaterials = [];
      });
  },
});

export const { clearError } = floorInwardSlice.actions;
export default floorInwardSlice.reducer;
